import React, { FC } from "react";
import { observer } from "mobx-react-lite";
import { makeStyles, Typography, Box, Tooltip } from "@material-ui/core";
import { Link } from "react-router-dom";
import { Parameter } from "./Parameter";
import { store } from "../store";
import Info from "@material-ui/icons/Info";
import { faqs } from "./FaqAccordion";
import { DropdownParameter } from "./DropdownParameter";
import { GEOMETRY, HORN_TYPES, THROAT_EXIT } from "../constants/enums";

const geometryOptions = [
  { label: "Circular", value: 0 },
  { label: "Rectangular", value: 1 },
  { label: "Elliptical", value: 2 },
];

export const hornType = [
  { label: "No diffraction slot", value: HORN_TYPES.NO_DIFFRACTION_SLOT },
  { label: "Diffraction horn", value: HORN_TYPES.ALLOW_DIFFRACTION_SLOT },
  { label: "Prioritise loading", value: HORN_TYPES.PRIORITISE_LOADING },
];

const throatExit = [
  { label: THROAT_EXIT.one, value: 0 },
  { label: THROAT_EXIT.oneFour, value: 1 },
  { label: THROAT_EXIT.two, value: 2 },
];

const InputParameters: FC<{ dynamic?: boolean; children?: React.ReactNode }> =
  observer(({ dynamic = false, children }) => {
    const classes = styles();
    const { tooltip } = classes;
    const {
      horizontalBeamWidth,
      verticalBeamWidth,
      horizontalMaxSize,
      verticalMaxSize,
      upperFrequency,
      selectedHornType,
      selectedThroatExit,
      handleChange,
      increment,
      decrement,
      isImperial,
      imperialHorizontalMaxSize,
      imperialVerticalMaxSize,
      imperialMaxLength,
      maxLength,
      geometry,
    } = store;

    const imperialChange = (name: string, value: number) =>
      handleChange(
        name,
        Number((isImperial ? value * 25.4 : value).toFixed(2))
      );

    const imperialIncrement =
      (value: number) => (name: string, scale: number) =>
        value % 1 > 0
          ? handleChange(name, Math.ceil(value) * 25.4)
          : increment(name, scale * 25.4);

    const imperialDecrement =
      (value: number) => (name: string, scale: number) =>
        value % 1 > 0
          ? handleChange(name, Math.floor(value) * 25.4)
          : decrement(name, scale * 25.4);

    return (
      <Box width={1} mt="20px">
        <Typography gutterBottom>Input your parameters</Typography>
        <Box className={`${classes.form} ${dynamic && classes.dynamic}`}>
          <Parameter
            handleChange={handleChange}
            parameter="Horizontal Beamwidth"
            name="horizontalBeamWidth"
            units="Degrees"
            min={20}
            max={150}
            increment={increment}
            decrement={decrement}
            value={horizontalBeamWidth}
            required
          />

          <Parameter
            handleChange={handleChange}
            parameter="Vertical Beamwidth"
            name="verticalBeamWidth"
            units="Degrees"
            disabled={GEOMETRY.Circular === geometry}
            min={20}
            max={150}
            increment={increment}
            decrement={decrement}
            value={verticalBeamWidth}
            required
          />

          <Parameter
            handleChange={imperialChange}
            parameter="Horizontal Max Size"
            name="horizontalMaxSize"
            units={isImperial ? "in" : "mm"}
            min={isImperial ? 70 / 25.4 : 70}
            max={isImperial ? 10000 / 25.4 : 10000}
            increment={
              isImperial
                ? imperialIncrement(imperialHorizontalMaxSize)
                : increment
            }
            decrement={
              isImperial
                ? imperialDecrement(imperialHorizontalMaxSize)
                : decrement
            }
            value={isImperial ? imperialHorizontalMaxSize : horizontalMaxSize}
            required
          />

          <Parameter
            handleChange={imperialChange}
            parameter="Vertical Max Size"
            name="verticalMaxSize"
            disabled={
              HORN_TYPES.PRIORITISE_LOADING === selectedHornType ||
              GEOMETRY.Circular === geometry
            }
            units={isImperial ? "in" : "mm"}
            min={isImperial ? 70 / 25.4 : 70}
            max={isImperial ? 10000 / 25.4 : 10000}
            increment={
              isImperial
                ? imperialIncrement(imperialVerticalMaxSize)
                : increment
            }
            decrement={
              isImperial
                ? imperialDecrement(imperialVerticalMaxSize)
                : decrement
            }
            value={isImperial ? imperialVerticalMaxSize : verticalMaxSize}
            required
          />

          <Parameter
            handleChange={imperialChange}
            parameter="Max Length"
            name="maxLength"
            disabled={HORN_TYPES.PRIORITISE_LOADING === selectedHornType}
            units={isImperial ? "in" : "mm"}
            min={isImperial ? 70 / 25.4 : 70}
            max={isImperial ? 10000 / 25.4 : 10000}
            increment={
              isImperial ? imperialIncrement(imperialMaxLength) : increment
            }
            decrement={
              isImperial ? imperialDecrement(imperialMaxLength) : decrement
            }
            value={isImperial ? imperialMaxLength : maxLength}
          />

          <Parameter
            handleChange={handleChange}
            parameter="Upper Frequency"
            name="upperFrequency"
            units="Hz"
            min={1000}
            max={20000}
            increment={increment}
            decrement={decrement}
            value={upperFrequency}
            required
            Tooltip={
              <Tooltip
                classes={{ tooltip }}
                title={faqs.find(({ id }) => id === "upperFrequency").answer}
              >
                <Link style={{ color: "inherit" }} to="/faq">
                  <Info />
                </Link>
              </Tooltip>
            }
          />

          <DropdownParameter
            handleChange={handleChange}
            parameter="Geometry"
            name="geometry"
            value={geometry}
            options={geometryOptions}
          />

          <DropdownParameter
            handleChange={handleChange}
            parameter="Horn type selector"
            name="selectedHornType"
            value={selectedHornType}
            options={hornType}
            disabled={GEOMETRY.Circular === geometry}
          />

          <DropdownParameter
            handleChange={handleChange}
            parameter="Throat Exit"
            name="selectedThroatExit"
            value={selectedThroatExit}
            options={throatExit}
          />
          {children}
        </Box>
      </Box>
    );
  });

const styles = makeStyles((theme) => ({
  form: {
    margin: "10px 0 40px 0",
  },
  tooltip: { fontSize: 14 },
  dynamic: {
    [theme.breakpoints.up("xs")]: {
      display: "grid",
      gridTemplateColumns: "repeat(1, 1fr)",
      gap: 20,
    },
    [theme.breakpoints.up("sm")]: {
      display: "grid",
      gridTemplateColumns: "repeat(2, 1fr)",
      gap: 20,
    },
    [theme.breakpoints.up("md")]: {
      display: "grid",
      gridTemplateColumns: "repeat(3, 1fr)",
      gap: 20,
    },
    [theme.breakpoints.up("lg")]: {
      display: "grid",
      gridTemplateColumns: "repeat(4, 1fr)",
      gap: 20,
    },
  },
}));

export default InputParameters;
